import type { BillableAction, SubscriptionTier } from "./types/constants";
import type { TierLimits } from "./types/limits";
import { BILLABLE_ACTIONS, SUBSCRIPTION_TIERS } from "./types/constants";

/**
 * Hard tier limit table. Shared between billing-service (server enforcement)
 * and apps/web (plan card rendering) so customer-facing copy can never drift
 * from the values authorization actually enforces.
 *
 * IMPORTANT — DEPLOYMENT CAP (active deployments) IS A PREVIEW GATE.
 * Every tier currently caps `deployment_active` at 1 on purpose. Per-hour
 * deployment billing is not yet enabled, so we intentionally do not let any
 * tier (paid or free) run more than one model deployment concurrently. When
 * per-hour credit billing for deployments ships, raise these caps and remove
 * this notice. Do NOT bump deployment limits in this file without enabling
 * the billing path first — overage settlement for deployments depends on it.
 *
 * IMPORTANT — STORAGE IS NOT METERED.
 * Training datasets, model artifacts (R2 / object storage), and trace dataset
 * exports are NOT counted, billed, or limited by any tier. There is no
 * `storage_bytes` BillableAction. If you add storage gating, also add a
 * BillableAction, a metric, a TIER_LIMITS column, and a settlement path —
 * do not pretend it's covered by these limits.
 *
 * The "enterprise" entry mostly mirrors growth and exists so type-exhaustive
 * lookups never return undefined for tier-cast values. Deliberate divergences
 * from growth: training_job.maxConcurrent (10 vs 2, INF-4838) and
 * eval_run_sample.maxConcurrent (50 vs 25). Real enterprise customers can
 * additionally run on custom plans / operator overrides which bypass these
 * limits.
 */
export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  [SUBSCRIPTION_TIERS.FREE]: {
    // Trainings are count-unlimited: every completion bills, and admission
    // is still gated by team billing status and postpaid exposure. Only
    // concurrency is capped (GPU capacity, not spend).
    training_job: {
      perHour: null,
      perDay: null,
      perMonth: null,
      maxConcurrent: 2,
    },
    // Sample counts are never capped: every eval sample bills at provider
    // cost + markup, so spend is gated on the team's credit balance at launch
    // (INF-4384), not on a count. ``maxConcurrent`` stays as an infra guard on
    // simultaneous run groups.
    eval_run_sample: {
      perHour: null,
      perDay: null,
      perMonth: null,
      maxConcurrent: 3,
    },
    // PREVIEW GATE: see file header. Do not bump without enabling billing.
    deployment_active: { perHour: 1, perDay: 1, perMonth: 1, maxConcurrent: 1 },
    deployment_gpu_hour: { perHour: null, perDay: null, perMonth: null },
    // RPM is enforced by the OpenAI-compatible REST middleware through
    // billing-v2 policy. The longer windows stay unbounded (null) until
    // request-volume quotas are explicitly designed.
    serverless_inference: {
      perMinute: 30,
      perHour: null,
      perDay: null,
      perMonth: null,
    },
    // Third-party serverless: mirror the hosted serverless_inference RPM so
    // a team gets the same per-minute ceiling on third-party calls (gpt-5.1,
    // claude-*, etc.) as on inference.net-hosted models. Edge enforcement
    // lives in apps/llm-ops-proxy/src/middleware/serverless-rate-limit.ts.
    third_party_serverless_inference: {
      perMinute: 30,
      perHour: null,
      perDay: null,
      perMonth: null,
    },
    proxied_inference_requests: {
      perHour: 50_000,
      perDay: null,
      perMonth: 1_000_000,
    },
    proxied_inference_data_bytes: {
      perHour: 2_500_000_000,
      perDay: null,
      perMonth: 50_000_000_000,
    },
    otel_span_count: {
      perHour: 50_000,
      perDay: null,
      perMonth: 1_000_000,
    },
    otel_span_data_bytes: {
      perHour: 2_500_000_000,
      perDay: null,
      perMonth: 50_000_000_000,
    },
    // Free-tier HALO's two included manual/follow-up runs are a lifetime team
    // allowance persisted on halo_runs, not a monthly window quota. Only
    // successful runs consume the grant — failed runs don't count.
    halo_run: { perHour: null, perDay: null, perMonth: null, maxConcurrent: 1 },
    // Metered, balance-gated. Unbounded at the tier level.
    halo_inference_cost: { perHour: null, perDay: null, perMonth: null },
    // Signals are a paid feature — 0 (none allowed) at every window. Free
    // teams also cannot create or activate signals at all
    // (SIGNALS_TIER_ENTITLEMENTS); these zeros are defense in depth.
    signal_span_classification: {
      perHour: 0,
      perDay: 0,
      perMonth: 0,
      includedPerMonth: 0,
    },
    signal_trace_classification: {
      perHour: 0,
      perDay: 0,
      perMonth: 0,
      includedPerMonth: 0,
    },
    signal_session_classification: {
      perHour: 0,
      perDay: 0,
      perMonth: 0,
      includedPerMonth: 0,
    },
  },
  [SUBSCRIPTION_TIERS.GROWTH]: {
    // See the FREE-tier note: count-unlimited, concurrency-capped.
    training_job: {
      perHour: null,
      perDay: null,
      perMonth: null,
      maxConcurrent: 2,
    },
    // Sample counts are never capped: every eval sample bills at provider
    // cost + markup, so spend is gated on the team's credit balance at launch
    // (INF-4384), not on a count. ``maxConcurrent`` stays as an infra guard on
    // simultaneous run groups.
    eval_run_sample: {
      perHour: null,
      perDay: null,
      perMonth: null,
      maxConcurrent: 25,
    },
    // PREVIEW GATE: see file header. Do not bump without enabling billing.
    deployment_active: { perHour: 1, perDay: 1, perMonth: 1, maxConcurrent: 1 },
    deployment_gpu_hour: { perHour: null, perDay: null, perMonth: null },
    serverless_inference: {
      perMinute: 1_000,
      perHour: null,
      perDay: null,
      perMonth: null,
    },
    third_party_serverless_inference: {
      perMinute: 1_000,
      perHour: null,
      perDay: null,
      perMonth: null,
    },
    proxied_inference_requests: {
      perHour: 2_500_000,
      perDay: null,
      perMonth: 50_000_000,
    },
    proxied_inference_data_bytes: {
      perHour: 125_000_000_000,
      perDay: null,
      perMonth: 2_500_000_000_000,
    },
    otel_span_count: {
      perHour: 2_500_000,
      perDay: null,
      perMonth: 50_000_000,
    },
    otel_span_data_bytes: {
      perHour: 125_000_000_000,
      perDay: null,
      perMonth: 2_500_000_000_000,
    },
    halo_run: { perHour: null, perDay: null, perMonth: null },
    halo_inference_cost: { perHour: null, perDay: null, perMonth: null },
    // Monthly hard cap = 5× includedPerMonth.
    signal_span_classification: {
      perHour: 50_000,
      perDay: null,
      perMonth: 500_000,
      includedPerMonth: 100_000,
    },
    signal_trace_classification: {
      perHour: 5_000,
      perDay: null,
      perMonth: 50_000,
      includedPerMonth: 10_000,
    },
    signal_session_classification: {
      perHour: 1_000,
      perDay: null,
      perMonth: 12_500,
      includedPerMonth: 2_500,
    },
  },
  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    // See the FREE-tier note: count-unlimited, concurrency-capped.
    // Enterprise gets a higher concurrency ceiling than free/growth
    // (INF-4838). NOTE: the prod k8s training cluster also has its own
    // fleet-wide concurrency capacity (k8s-provider capabilities), so jobs
    // beyond that queue for cluster capacity even when billing admits them.
    training_job: {
      perHour: null,
      perDay: null,
      perMonth: null,
      maxConcurrent: 10,
    },
    // Sample counts are never capped: every eval sample bills at provider
    // cost + markup, so spend is gated on the team's credit balance at launch
    // (INF-4384), not on a count. ``maxConcurrent`` stays as an infra guard on
    // simultaneous run groups.
    eval_run_sample: {
      perHour: null,
      perDay: null,
      perMonth: null,
      maxConcurrent: 50,
    },
    deployment_active: { perHour: 1, perDay: 1, perMonth: 1, maxConcurrent: 1 },
    deployment_gpu_hour: { perHour: null, perDay: null, perMonth: null },
    serverless_inference: {
      perMinute: 1_000,
      perHour: null,
      perDay: null,
      perMonth: null,
    },
    third_party_serverless_inference: {
      perMinute: 1_000,
      perHour: null,
      perDay: null,
      perMonth: null,
    },
    proxied_inference_requests: {
      perHour: 2_500_000,
      perDay: null,
      perMonth: 50_000_000,
    },
    proxied_inference_data_bytes: {
      perHour: 125_000_000_000,
      perDay: null,
      perMonth: 2_500_000_000_000,
    },
    otel_span_count: {
      perHour: 2_500_000,
      perDay: null,
      perMonth: 50_000_000,
    },
    otel_span_data_bytes: {
      perHour: 125_000_000_000,
      perDay: null,
      perMonth: 2_500_000_000_000,
    },
    // Mirrors GROWTH (see note there); enterprise tier-cast values fall back
    // to this, while real enterprise customers run on custom plans that bypass
    // these limits.
    halo_run: { perHour: null, perDay: null, perMonth: null },
    halo_inference_cost: { perHour: null, perDay: null, perMonth: null },
    signal_span_classification: {
      perHour: 50_000,
      perDay: null,
      perMonth: 500_000,
      includedPerMonth: 100_000,
    },
    signal_trace_classification: {
      perHour: 5_000,
      perDay: null,
      perMonth: 50_000,
      includedPerMonth: 10_000,
    },
    signal_session_classification: {
      perHour: 1_000,
      perDay: null,
      perMonth: 12_500,
      includedPerMonth: 2_500,
    },
  },
};

/**
 * Free-tier-only clamps applied to per-launch HALO parameters. Paid tiers send
 * `maxTurns` / `maxSubagentDepth` through unchanged; the launch procedure caps
 * them against these values for free-tier teams so a single bored user can't
 * burn unbounded inference + sandbox time from their trial credit grant.
 */
export const HALO_FREE_TIER_CAPS = {
  maxTurns: 25,
  maxSubagentDepth: 3,
} as const;

/**
 * Per-tier caps on signal CONFIGURATION (not metered usage): how many signals
 * a team may have, how many of those may be custom (built from scratch rather
 * than a platform template), and whether regex classifiers are available.
 *
 * Unlike TIER_LIMITS windows, `null` means unlimited and `0` means none
 * allowed — free teams cannot create signals at all (signals are a paid
 * feature). Enforced by the signals service at signal create time;
 * custom-plan teams use the ENTERPRISE column.
 */
export interface SignalsTierEntitlements {
  /** Max non-archived signals per team; null = unlimited, 0 = none. */
  maxSignals: number | null;
  /** Max non-archived custom (non-template) signals; null = unlimited. */
  maxCustomSignals: number | null;
  /** Whether regex classifier signals may be created. */
  regexEnabled: boolean;
}

export const SIGNALS_TIER_ENTITLEMENTS: Record<
  SubscriptionTier,
  SignalsTierEntitlements
> = {
  [SUBSCRIPTION_TIERS.FREE]: {
    maxSignals: 0,
    maxCustomSignals: 0,
    regexEnabled: false,
  },
  [SUBSCRIPTION_TIERS.GROWTH]: {
    maxSignals: null,
    maxCustomSignals: null,
    regexEnabled: true,
  },
  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    maxSignals: null,
    maxCustomSignals: null,
    regexEnabled: true,
  },
};

/**
 * Monthly included credit grant per tier, in USD cents. Independent of
 * TIER_LIMITS — these are credits the platform deposits, not caps it
 * enforces. Currently only Free has a fixed grant ($1) as a trial allowance;
 * paid tier grants are TBD pending business decision and are intentionally
 * left at 0 rather than guessed.
 */
export const TIER_INCLUDED_CREDITS_USD_CENTS: Record<SubscriptionTier, number> =
  {
    [SUBSCRIPTION_TIERS.FREE]: 100,
    [SUBSCRIPTION_TIERS.GROWTH]: 0,
    [SUBSCRIPTION_TIERS.ENTERPRISE]: 0,
  };

/**
 * One-time prepaid credit awarded after a team's first successful Growth
 * subscription checkout. This is an opening credit, not a recurring monthly
 * included-credit grant.
 */
export const GROWTH_SUBSCRIPTION_OPENING_CREDIT_USD_CENTS = 5_000;

export type BillableActionUnitKind = "count" | "bytes";

export interface BillableActionMetadata {
  /** Short label shown on plan cards and usage rows. */
  label: string;
  /** Tooltip / longer description disambiguating the action for customers. */
  description: string;
  /** Whether the action is a concurrent / max-active limit rather than a windowed counter. */
  isConcurrency: boolean;
  /** Units for the windowed counter, used by formatters. */
  unitKind: BillableActionUnitKind;
}

/**
 * Customer-facing metadata for each billable action. Plan cards and usage
 * rows pull labels and tooltip copy from here so wording stays consistent
 * across the dashboard and so future actions add a single entry instead of
 * multiple hardcoded strings.
 */
export const BILLABLE_ACTION_METADATA: Record<
  BillableAction,
  BillableActionMetadata
> = {
  [BILLABLE_ACTIONS.DEPLOYMENT_ACTIVE]: {
    label: "Active deployments",
    description:
      "Number of model deployments that can be online at the same time. Limited to 1 across all tiers while per-hour deployment billing is in preview — higher caps unlock once credit-metered deployments ship.",
    isConcurrency: true,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.DEPLOYMENT_GPU_HOUR]: {
    label: "Deployment GPU hours",
    description:
      "Runtime usage for active deployments, measured by GPU type and elapsed active time. Deployment slots remain controlled by Active deployments.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.TRAINING_JOB]: {
    label: "Training jobs",
    description:
      "Fine-tuning and LoRA training runs you can launch per month. Each launched job counts once regardless of duration.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.EVAL_RUN_SAMPLE]: {
    label: "Eval samples",
    description:
      "Individual eval run samples graded against your tasks. One sample is one judged inference.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.SERVERLESS_INFERENCE]: {
    label: "Serverless inference",
    description:
      "Pay-as-you-go inference requests against the inference.net serverless models. Billed per token from your credit balance.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.THIRD_PARTY_SERVERLESS_INFERENCE]: {
    label: "Third-party serverless inference",
    description:
      "Pay-as-you-go inference requests routed to third-party upstream providers (OpenAI, Anthropic, Google, etc.) from your inference.net credit balance. Billed per token at the provider's published rate.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.PROXIED_INFERENCE_REQUESTS]: {
    label: "Gateway inference requests",
    description:
      "Inference requests routed through the inference.net Gateway proxy to your configured upstream providers. Counts the request to the Gateway, not the underlying provider call.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.PROXIED_INFERENCE_DATA_BYTES]: {
    label: "Gateway inference data",
    description:
      "Total bytes of request + response payloads passing through the Gateway proxy. Headers excluded.",
    isConcurrency: false,
    unitKind: "bytes",
  },
  [BILLABLE_ACTIONS.OTEL_SPAN_COUNT]: {
    label: "OTEL spans",
    description:
      "Observability spans ingested via the OpenTelemetry endpoint for trace and agent visibility.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.OTEL_SPAN_DATA_BYTES]: {
    label: "OTEL span data",
    description:
      "Total bytes of attributes and payloads attached to ingested OpenTelemetry spans.",
    isConcurrency: false,
    unitKind: "bytes",
  },
  [BILLABLE_ACTIONS.HALO_RUN]: {
    label: "HALO runs",
    description:
      "Agent runs launched on the HALO runtime. Each successful run incurs one flat fee regardless of duration; failed runs are not charged. Paid tiers are capped at a fixed number of runs per day.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.HALO_INFERENCE_COST]: {
    label: "HALO inference",
    description:
      "Provider inference cost for LLM requests issued by HALO runs, marked up over our wholesale provider rate.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.SIGNAL_SPAN_CLASSIFICATION]: {
    label: "Span signal classifications",
    description:
      "Individual spans classified by your signals. One classification is one span evaluated against one signal.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.SIGNAL_TRACE_CLASSIFICATION]: {
    label: "Trace signal classifications",
    description:
      "Whole traces classified by your trace-scoped signals. One classification is one trace evaluated against one signal.",
    isConcurrency: false,
    unitKind: "count",
  },
  [BILLABLE_ACTIONS.SIGNAL_SESSION_CLASSIFICATION]: {
    label: "Session signal classifications",
    description:
      "Whole sessions classified by your session-scoped signals. One classification is one session evaluated against one signal.",
    isConcurrency: false,
    unitKind: "count",
  },
};

/**
 * Stable display order for billable actions in customer-facing UI. Ordered
 * concurrency first (capacity), then training/eval (foreground work),
 * then gateway / OTEL (high-volume background usage).
 */
export const BILLABLE_ACTION_DISPLAY_ORDER: readonly BillableAction[] = [
  BILLABLE_ACTIONS.DEPLOYMENT_ACTIVE,
  BILLABLE_ACTIONS.DEPLOYMENT_GPU_HOUR,
  BILLABLE_ACTIONS.TRAINING_JOB,
  BILLABLE_ACTIONS.EVAL_RUN_SAMPLE,
  BILLABLE_ACTIONS.HALO_RUN,
  BILLABLE_ACTIONS.HALO_INFERENCE_COST,
  BILLABLE_ACTIONS.SERVERLESS_INFERENCE,
  BILLABLE_ACTIONS.THIRD_PARTY_SERVERLESS_INFERENCE,
  BILLABLE_ACTIONS.PROXIED_INFERENCE_REQUESTS,
  BILLABLE_ACTIONS.PROXIED_INFERENCE_DATA_BYTES,
  BILLABLE_ACTIONS.OTEL_SPAN_COUNT,
  BILLABLE_ACTIONS.OTEL_SPAN_DATA_BYTES,
  BILLABLE_ACTIONS.SIGNAL_SPAN_CLASSIFICATION,
  BILLABLE_ACTIONS.SIGNAL_TRACE_CLASSIFICATION,
  BILLABLE_ACTIONS.SIGNAL_SESSION_CLASSIFICATION,
];
